import type { PayloadAction } from "@reduxjs/toolkit";
import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import axios from "axios";
import { isAppUnavailablePayload, toastApiError } from "helpers/apiErrorToast";
import { downloadFile, getCleanStartorEndDay, getDateForFileName, translationsWithFallback } from "helpers/utils";
import { t } from "i18next";
import { toast } from "react-toastify";
import type { IDatesRange } from "types";

import { createAppAsyncThunk } from "./appAsyncThunk";
import type { TFetchStatus } from "./types";

export type ProvisionImpactedTerm = {
    id: number;
    initialProvision: number | null;
    reinstatedProvisionPercent: number | null;
};

/** Client-side replica of the backend termCanReinstate logic */
export const termCanReinstate = (term: ProvisionImpactedTerm): boolean =>
    (term.initialProvision ?? 0) > 0 || term.reinstatedProvisionPercent === 10000;

export type TPeriod = {
    companyId: number;
    id: number;
    periodEnd: Date;
    periodStart: Date;
    isActive: number;
    minimum: number;
    periodDuration: number;
    periodTypeId: number;
    isClosedWithCarryForward: number | boolean;
};

export interface IPeriod {
    id: number;
    periodStart: Date;
    periodEnd: Date;
    companyId: number;
    minimum: number;
    isActive: number | boolean;
    periodTypeId: number;
    createdAt: Date;
    isClosedWithCarryForward: number | boolean;
}

export interface PeriodWithComposites {
    composite: IPeriod;
    components: IPeriod[];
}

export interface PeriodExchangeRates {
    periodId: number;
    companyId: number;
    currency: string;
    rate: number;
}

export const unclosePreviousPeriod = createAppAsyncThunk(
    "periods/unclosePreviousPeriod",
    async (previousPeriodId: number, { rejectWithValue }) => {
        try {
            await axios.post(import.meta.env.REACT_APP_API_ENDPOINT + `/period/${previousPeriodId}/reopen`);
            toast.success(`The previous period has been successfully reopened`);
        } catch (error: any) {
            toast.error("Failed to reopen previous period");
            return rejectWithValue(error.response.data);
        }
    },
);

export const fetchPeriods = createAsyncThunk("periods/getPeriods", async (_, { rejectWithValue }) => {
    try {
        const response = await axios.get(import.meta.env.REACT_APP_API_ENDPOINT + "/period");
        return response.data.result;
    } catch (error: any) {
        return rejectWithValue(error.response.data);
    }
});

export const fetchPeriodsWithComposites = createAsyncThunk(
    "periods/getPeriodsWithComposites",
    async (_, { rejectWithValue }) => {
        try {
            const response = await axios.get(import.meta.env.REACT_APP_API_ENDPOINT + "/period/composites");
            return response.data.data;
        } catch (error: any) {
            return rejectWithValue(error.response.data);
        }
    },
);

export const fetchPeriodTypes = createAsyncThunk("periods/fetchPeriodTypes", async (_, { rejectWithValue }) => {
    try {
        const response = await axios.get(import.meta.env.REACT_APP_API_ENDPOINT + "/period/types");
        return response.data.data;
    } catch (error: any) {
        return rejectWithValue(error.response.data);
    }
});

export const fetchClosePeriod = createAsyncThunk(
    "periods/fetchClosePeriod",
    async (periodId: number, { rejectWithValue }) => {
        try {
            const response = await axios.post(import.meta.env.REACT_APP_API_ENDPOINT + `/period/close`, { periodId });
            return response.data.data;
        } catch (error: any) {
            return rejectWithValue(error.response.data);
        }
    },
);

export const addGlobalProvision = createAsyncThunk(
    "periods/addGlobalProvision",
    async (
        data: {
            periodId: number;
            payload: { provisionPercent?: number; reinstatePercent?: number; releasedSince?: string };
        },
        { rejectWithValue },
    ) => {
        try {
            const response = await axios.put(
                import.meta.env.REACT_APP_API_ENDPOINT + "/period/" + data.periodId,
                data.payload,
            );
            return response.data;
        } catch (error: any) {
            return rejectWithValue(error.response.data);
        }
    },
);

export const fetchGlobalProvisionImpactedTerms = createAsyncThunk(
    "periods/fetchGlobalProvisionImpactedTerms",
    async ({ periodId, releasedSince }: { periodId: number; releasedSince?: string }, { rejectWithValue }) => {
        try {
            const response = await axios.get(
                import.meta.env.REACT_APP_API_ENDPOINT + `/period/${periodId}/provision/impacted-terms`,
                { params: releasedSince ? { releasedSince } : undefined },
            );
            return response.data.data as ProvisionImpactedTerm[];
        } catch (error: any) {
            return rejectWithValue(error.response.data);
        }
    },
);

export const putMinimum = createAsyncThunk(
    "periods/putMinimum",
    async ({ amount, period }: { amount: number; period: number | undefined }, { rejectWithValue }) => {
        try {
            const response = await axios.put(import.meta.env.REACT_APP_API_ENDPOINT + `/period/${period}`, {
                minimum: +amount * 100,
            });
            return response.data;
        } catch (error: any) {
            return rejectWithValue(error.response.data);
        }
    },
);

export const putPeriodClosedWithCarryForward = createAsyncThunk(
    "periods/putClosedWithCarryForward",
    async (
        { periodId, isClosedWithCarryForward }: { periodId: number; isClosedWithCarryForward: boolean },
        { rejectWithValue },
    ) => {
        try {
            const response = await axios.put(import.meta.env.REACT_APP_API_ENDPOINT + `/period/${periodId}`, {
                isClosedWithCarryForward: isClosedWithCarryForward ? 1 : 0,
            });
            return response.data;
        } catch (error: any) {
            return rejectWithValue(error.response.data);
        }
    },
);

export const fetchExportUnpaidAccounting = createAsyncThunk(
    "period/exportUnpaidAccounting",
    async (params: { periodId: number }, { rejectWithValue }) => {
        try {
            const response = await toast.promise(
                axios.get(import.meta.env.REACT_APP_API_ENDPOINT + `/period/accounting/unpaid`, {
                    params,
                    headers: { Accept: "*/*" },
                    responseType: "blob",
                }),
                {
                    pending: `${t("Export.Pending")}`,
                    success: `${t("Export.Successful")}`,
                    error: `${t("Export.Error")}`,
                },
            );

            const fileName = `export_comptable_report_${getDateForFileName()}.zip`;
            downloadFile({ source: response.data, fileName });
        } catch (error: any) {
            return rejectWithValue(error.response.data);
        }
    },
);

export const recomputeRoyalties = createAsyncThunk(
    "periods/recomputeRoyalties",
    async (periodId: number, { rejectWithValue }) => {
        try {
            const response = await axios.post(
                import.meta.env.REACT_APP_API_ENDPOINT + `/period/${periodId}/recompute-royalties`,
            );
            return response.data.data;
        } catch (error: any) {
            return rejectWithValue(error.response.data);
        }
    },
);

export const fetchPeriodExchangeRates = createAsyncThunk(
    "periods/getPeriodExchangeRates",
    async ({ periodId }: { periodId: number }, { rejectWithValue }) => {
        try {
            const response = await axios.get(
                import.meta.env.REACT_APP_API_ENDPOINT + `/period/${periodId}/exchange-rates`,
            );
            return response.data.data;
        } catch (error: any) {
            return rejectWithValue(error.response.data);
        }
    },
);

export const putPeriodExchangeRates = createAsyncThunk(
    "periods/putPeriodExchangeRates",
    async ({ periodId, exchangeRates }: { periodId: number; exchangeRates: any }, { rejectWithValue }) => {
        try {
            const response = await axios.put(
                import.meta.env.REACT_APP_API_ENDPOINT + `/period/${periodId}/exchange-rates`,
                exchangeRates,
            );
            return response.data.data;
        } catch (error: any) {
            return rejectWithValue(error.response.data);
        }
    },
);

export const fetchECBCurrencyData = createAsyncThunk(
    "periods/getECBCurrencyData",
    async ({ currency, date, periodId }: { currency: string; date: string; periodId: number }, { rejectWithValue }) => {
        try {
            const response = await axios.get(
                import.meta.env.REACT_APP_API_ENDPOINT +
                    `/period/${periodId}/exchange-rates/ecb/${currency}?startPeriod=${date}&endPeriod=${date}`,
            );
            return response.data.data.dataSets[0].series["0:0:0:0:0"].observations[0][0];
        } catch (error: any) {
            return rejectWithValue(error);
        }
    },
);

export const fetchDeferredPaymentAccounting = createAsyncThunk(
    "periods/getDeferredPaymentAccounting",
    async (periodId: number, { rejectWithValue }) => {
        try {
            const response = await toast.promise(
                axios.get(import.meta.env.REACT_APP_API_ENDPOINT + `/period/accounting/unpaid?periodId=${periodId}`, {
                    headers: { Accept: "*/*" },
                    responseType: "blob",
                }),
                {
                    pending: `${t("Export.Pending")}`,
                    success: `${t("Export.Successful")}`,
                    error: `${t("Export.NoDeferTypology")}`,
                },
            );
            const fileName = `export_comptable_deferred_payment${getDateForFileName()}.zip`;
            downloadFile({ source: response.data, fileName });
        } catch (error: any) {
            return rejectWithValue(error);
        }
    },
);

export interface PeriodType {
    id: number;
    duration: number;
}

export interface PeriodState {
    periods: TPeriod[];
    periodsDateRange: IDatesRange;
    periodsWithComposites: PeriodWithComposites[];
    periodTypes: PeriodType[];
    activePeriod: undefined | number; // Selected period in the front-end
    activePeriodData: TPeriod | undefined;
    isLoading: boolean;
    closePeriodStatus: TFetchStatus;
    addGlobalProvisionStatus: TFetchStatus;
    globalProvisionImpactedTerms: ProvisionImpactedTerm[];
    globalProvisionImpactedTermsStatus: TFetchStatus;
    periodExchangeRates: PeriodExchangeRates[];
    exchangeRate: undefined | number;
    periodsFetchStatus: TFetchStatus;
    periodsWithCompositesFetchStatus: TFetchStatus;
    currentPeriod: TPeriod | undefined; // Period on the company-side
    currentPeriodDatesRange: IDatesRange;
    allPeriods: TPeriod[];
    recomputeRoyaltiesStatus: TFetchStatus;
}

const initialState: PeriodState = {
    periods: [],
    periodsDateRange: {} as IDatesRange,
    periodsWithComposites: [],
    periodTypes: [],
    activePeriod: undefined,
    activePeriodData: undefined,
    isLoading: true,
    closePeriodStatus: "idle",
    addGlobalProvisionStatus: "idle",
    globalProvisionImpactedTerms: [],
    globalProvisionImpactedTermsStatus: "idle",
    periodExchangeRates: [],
    exchangeRate: undefined,
    periodsFetchStatus: "idle",
    periodsWithCompositesFetchStatus: "idle",
    currentPeriod: undefined,
    currentPeriodDatesRange: {} as IDatesRange,
    allPeriods: [] as TPeriod[],
    recomputeRoyaltiesStatus: "idle",
};

export const periodSlice = createSlice({
    name: "period",
    initialState,
    reducers: {
        setActivePeriod: (state, action) => {
            state.activePeriod = action.payload;
            state.activePeriodData = state.allPeriods.find((period) => period.id === action.payload);
            localStorage.setItem("activePeriod", action.payload.toString());
        },
        setClosePeriodIdleStatus(state) {
            state.closePeriodStatus = "idle";
        },
        setRecomputeRoyaltiesStatus(state, action: PayloadAction<TFetchStatus>) {
            state.recomputeRoyaltiesStatus = action.payload;
        },
    },
    extraReducers: (builder) => {
        builder.addCase(fetchPeriods.pending, (state, action) => {
            state.periodsFetchStatus = "pending";
        });
        builder.addCase(fetchPeriods.fulfilled, (state, action: PayloadAction<TPeriod[]>) => {
            const fetchedPeriods = action.payload ?? [];
            const periodsWithoutPending = fetchedPeriods.filter((period) => period.isActive !== 2);
            state.periods = periodsWithoutPending;
            state.allPeriods = fetchedPeriods;

            if (periodsWithoutPending.length) {
                // Take first day of first period of account and set it to start of day:
                const startDate = getCleanStartorEndDay(
                    periodsWithoutPending[periodsWithoutPending.length - 1].periodStart,
                );
                // Get end date of current active period.
                const activePeriodEnd = getCleanStartorEndDay(periodsWithoutPending[0].periodEnd, { time: "end" });

                // Format periods date range
                state.periodsDateRange = {
                    startDate,
                    endDate: activePeriodEnd,
                };
            }
            const localeActivePeriod = +(localStorage.getItem("activePeriod") || "-1");
            const isLastLocaleActivePeriodActive =
                periodsWithoutPending.find((item) => item.id === localeActivePeriod)?.isActive == 1;
            if (!isLastLocaleActivePeriodActive) localStorage.removeItem("activePeriod");
            if (!state.activePeriod) {
                const newActivePeriod =
                    (isLastLocaleActivePeriodActive && localeActivePeriod) ||
                    periodsWithoutPending.find((item) => item.isActive === 1)?.id;
                if (newActivePeriod) {
                    state.activePeriod = newActivePeriod;
                    state.activePeriodData = state.periods.find((period) => period.id === newActivePeriod);
                    localStorage.setItem("activePeriod", newActivePeriod.toString());
                }
                state.currentPeriod = periodsWithoutPending.find((item) => item.isActive === 1);
            } else {
                state.currentPeriod = state.allPeriods.find((period) => period.id == state.activePeriod);
                localStorage.setItem("activePeriod", state.activePeriod.toString());
            }

            if (state.currentPeriod) {
                // set startDate to beginning of the day
                const startDate = getCleanStartorEndDay(state.currentPeriod?.periodStart ?? new Date());
                // set endDate to end of the day
                const endDate = getCleanStartorEndDay(state.currentPeriod?.periodEnd, { time: "end" });

                // Do NOT mutate currentPeriod.periodStart/periodEnd: currentPeriod shares
                // its reference with the entry in allPeriods, and the backend ships UTC
                // midnight ISO. Overwriting with a local-time Date shifts the UTC calendar
                // day by -1 in positive timezones (dateConvert reads getUTC* parts).

                state.currentPeriodDatesRange = {
                    startDate,
                    endDate,
                };
            }

            state.isLoading = false;
            state.periodsFetchStatus = "succeeded";
        });
        builder.addCase(fetchPeriods.rejected, (state, { payload }) => {
            toastApiError(payload, "ServerError.InternalServerError");
            state.isLoading = false;
            state.periodsFetchStatus = "failed";
        });

        builder.addCase(fetchPeriodsWithComposites.pending, (state) => {
            state.periodsWithCompositesFetchStatus = "pending";
        });
        builder.addCase(
            fetchPeriodsWithComposites.fulfilled,
            (state, action: PayloadAction<PeriodWithComposites[]>) => {
                state.periodsWithComposites = action.payload;
                state.periodsWithCompositesFetchStatus = "succeeded";
            },
        );
        builder.addCase(fetchPeriodsWithComposites.rejected, (state, { payload }) => {
            toastApiError(payload, "ServerError.InternalServerError");
            state.periodsWithCompositesFetchStatus = "failed";
        });

        builder.addCase(fetchPeriodTypes.fulfilled, (state, action) => {
            state.periodTypes = action.payload;
        });
        builder.addCase(fetchPeriodTypes.rejected, (state, { payload }) => {
            toastApiError(payload, "ServerError.InternalServerError");
        });

        builder.addCase(fetchClosePeriod.pending, (state) => {
            state.closePeriodStatus = "pending";
        });
        builder.addCase(fetchClosePeriod.fulfilled, (state) => {
            toast.success(`${t("Management.SuccessfullyClosed")}`);
            state.closePeriodStatus = "succeeded";
            state.activePeriod = undefined;
            localStorage.removeItem("activePeriod");
        });
        builder.addCase(fetchClosePeriod.rejected, (state, { payload }) => {
            toastApiError(payload, "ServerError.InternalServerError");
            state.closePeriodStatus = "failed";
        });

        builder.addCase(addGlobalProvision.pending, (state) => {
            state.addGlobalProvisionStatus = "pending";
        });
        builder.addCase(addGlobalProvision.fulfilled, (state) => {
            toast.success(`${t("Management.AddProvisionsSuccess")}`);
            state.addGlobalProvisionStatus = "succeeded";
        });
        builder.addCase(addGlobalProvision.rejected, (state, { payload }) => {
            toastApiError(payload, "ServerError.InternalServerError");
            state.addGlobalProvisionStatus = "failed";
        });

        builder.addCase(fetchGlobalProvisionImpactedTerms.pending, (state) => {
            state.globalProvisionImpactedTermsStatus = "pending";
            state.globalProvisionImpactedTerms = [];
        });
        builder.addCase(
            fetchGlobalProvisionImpactedTerms.fulfilled,
            (state, action: PayloadAction<ProvisionImpactedTerm[]>) => {
                state.globalProvisionImpactedTerms = action.payload;
                state.globalProvisionImpactedTermsStatus = "succeeded";
            },
        );
        builder.addCase(fetchGlobalProvisionImpactedTerms.rejected, (state) => {
            state.globalProvisionImpactedTermsStatus = "failed";
        });

        builder.addCase(putMinimum.pending, () => {});
        builder.addCase(putMinimum.fulfilled, () => {
            toast.success(`${t("Management.PutMinimumSuccess")}`);
        });
        builder.addCase(putMinimum.rejected, (_, { payload }) => {
            toastApiError(payload, "ServerError.InternalServerError");
        });

        builder.addCase(fetchExportUnpaidAccounting.pending, (state) => {
            // state.exportFileStatus = "pending";
        });
        builder.addCase(fetchExportUnpaidAccounting.fulfilled, (state) => {
            toast.success(`${t("Payment.SuccessExport")}`);
            // state.exportFileStatus = "succeeded";
        });
        builder.addCase(fetchExportUnpaidAccounting.rejected, (state) => {
            // state.exportFileStatus = "failed";
        });

        builder.addCase(fetchPeriodExchangeRates.pending, (state, action) => {
            state.periodExchangeRates = [];
        });
        builder.addCase(fetchPeriodExchangeRates.fulfilled, (state, action) => {
            state.periodExchangeRates = action.payload;
        });
        builder.addCase(fetchPeriodExchangeRates.rejected, (state, action) => {
            const translation = translationsWithFallback(
                (action.payload as any)?.message,
                "ServerError.InternalServerError",
            );
            if (!isAppUnavailablePayload(action.payload)) {
                toast.error(`${translation}`, { toastId: "ServerError.InternalServerError" });
            }
        });
        builder.addCase(putPeriodExchangeRates.fulfilled, (state, action) => {
            toast.success(`${t("Settings.EditCurrencyRatesSuccess")}`);
        });
        builder.addCase(putPeriodExchangeRates.rejected, (state, action) => {
            const translation = translationsWithFallback(
                (action.payload as any)?.message,
                "ServerError.InternalServerError",
            );
            if (!isAppUnavailablePayload(action.payload)) {
                toast.error(`${translation}`, { toastId: "ServerError.InternalServerError" });
            }
        });
        builder.addCase(fetchECBCurrencyData.fulfilled, (state, action) => {
            state.exchangeRate = action.payload;
        });
        builder.addCase(fetchECBCurrencyData.rejected, (state, action) => {
            state.exchangeRate = undefined;
        });
        builder.addCase(recomputeRoyalties.rejected, (state, action) => {
            const translation = translationsWithFallback(
                (action.payload as any)?.message,
                "ServerError.InternalServerError",
            );
            if (!isAppUnavailablePayload(action.payload)) {
                toast.error(`${translation}`, { toastId: "ServerError.InternalServerError" });
            }
            state.recomputeRoyaltiesStatus = "failed";
        });
    },
});

// Action creators are generated for each case reducer function
export const { setActivePeriod, setClosePeriodIdleStatus, setRecomputeRoyaltiesStatus } = periodSlice.actions;

export default periodSlice.reducer;
